import React from 'react';

const TeamStatusCard = ({ teamStatus, isLoading, error }) => {
  if (isLoading) {
    return <div className="text-center py-6">Loading team status...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        Error: {error}
      </div>
    );
  }

  if (!teamStatus) {
    return null;
  }

  const { members, summary } = teamStatus;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
      case 'locked':
        return (
          <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
            ✅ Submitted
          </span>
        );
      case 'draft':
        return (
          <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
            📝 Draft
          </span>
        );
      default:
        return (
          <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            ⏳ Not Submitted
          </span>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Summary Stats */}
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Team Submission Status</h3>
        
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 p-4 rounded">
            <p className="text-gray-600 text-sm">Total Members</p>
            <p className="text-2xl font-bold text-blue-600">{summary.total}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded">
            <p className="text-gray-600 text-sm">Submitted</p>
            <p className="text-2xl font-bold text-green-600">{summary.submitted}</p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded">
            <p className="text-gray-600 text-sm">Missing</p>
            <p className="text-2xl font-bold text-yellow-600">{summary.missing}</p>
          </div>
          
          <div className={`p-4 rounded ${summary.isComplete ? 'bg-green-100' : 'bg-orange-100'}`}>
            <p className="text-gray-600 text-sm">Status</p>
            <p className={`text-lg font-bold ${summary.isComplete ? 'text-green-600' : 'text-orange-600'}`}>
              {summary.isComplete ? '✅ Complete' : '⏳ Pending'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              summary.isComplete ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${(summary.submitted / summary.total) * 100}%` }}
          />
        </div>
        <p className="text-xs text-gray-600 mt-2">
          {summary.submitted} of {summary.total} members submitted
        </p>
      </div>

      {/* Team Members List */}
      <div className="p-6">
        <h4 className="font-semibold text-gray-800 mb-4">Team Members</h4>
        
        <div className="space-y-3">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex-1">
                <p className="font-medium text-gray-800">{member.full_name}</p>
                <p className="text-xs text-gray-500">{member.email}</p>
              </div>
              
              <div className="flex items-center gap-2">
                {member.is_required ? (
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                    Required
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-gray-600 bg-gray-200 px-2 py-1 rounded">
                    Optional
                  </span>
                )}
                {getStatusBadge(member.report_status)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Completion Message */}
      {summary.isComplete && (
        <div className="p-4 bg-green-50 border-t border-green-200">
          <p className="text-green-800 font-semibold">
            ✅ All team members have submitted their reports! The team summary will be sent to Teams.
          </p>
        </div>
      )}

      {summary.missing > 0 && (
        <div className="p-4 bg-yellow-50 border-t border-yellow-200">
          <p className="text-yellow-800 font-semibold">
            ⏳ Waiting for {summary.missing} more member{summary.missing > 1 ? 's' : ''} to submit.
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamStatusCard;
